<script lang="ts">
  import Dialog, { Header } from '@smui/dialog';
  import Icon from '../../common/components/Icon.svelte';
  import VideoPlayer from 'svelte-video-player';
  import PdpProductGallery360View from './pdpProductGallery360View.svelte';
  import { productDetails, productImages } from '../store/pdp_store';
  import { getAppConfig } from '../../../common/config/app-config';
  import { filterImagesPreferArabic } from '../services/pdp-data-services';

  const { lang } = getAppConfig();

  export let open = false;
  export let initialIndex = 0;
  export let galleryItems: Array<string | { src: string; type?: string; label?: string }> = [];
  export let galleryMode: 'product' | 'review' = 'product';
  export let galleryImageAlt = 'Product image';
  export let onClose: (() => void) | undefined = undefined;

  let zoomScale = 1;
  const minZoom = 1;
  const maxZoom = 3;
  const zoomStep = 0.25;
  let activeIndex = 0;
  let selectedTile = 'PRODUCT_IMAGES';

  function normalizeGalleryItems(items) {
    return (items || []).map(item => (typeof item === 'string' ? { src: item, type: 'image' } : item)).filter(Boolean);
  }

  $: galleryImages = galleryMode === 'review' ? normalizeGalleryItems(galleryItems) : $productImages || [];
  $: filteredGalleryImages = filterImagesPreferArabic(galleryImages, lang);
  $: totalCount = filteredGalleryImages?.length;
  $: code = $productDetails?.code;
  $: extraProductGalleryTiles = $productDetails?.extraProductGalleryTiles || [];
  $: data360View = extraProductGalleryTiles.find(tile => tile?.code === '360_VIEW');
  $: has360ViewImage = ($productImages || []).some(image => image?.type === 'set');
  $: has360ViewTile = Boolean(data360View && has360ViewImage);
  $: totalSlides = has360ViewTile ? totalCount + 1 : totalCount;
  $: is360 = selectedTile === '360_VIEW';
  $: activeImageIndex = Math.max(activeIndex - (has360ViewTile ? 1 : 0), 0);
  $: currentItem = filteredGalleryImages?.[activeImageIndex];
  $: imageLabel = is360 ? `360 View` : totalCount ? `${activeImageIndex + 1} / ${totalCount}` : `0 / 0`;

  $: if (open && (totalCount || has360ViewTile)) {
    const initialGalleryIndex =
      galleryMode === 'review'
        ? initialIndex
        : ($productImages || []).slice(0, initialIndex + 1).filter(image => image?.type !== 'set').length - 1;
    activeIndex = Math.min(
      Math.max(has360ViewTile ? initialGalleryIndex + 1 : initialGalleryIndex, 0),
      Math.max((has360ViewTile ? totalCount + 1 : totalCount) - 1, 0)
    );
    selectedTile = 'PRODUCT_IMAGES';
    zoomScale = 1;
  }

  function closeModal() {
    open = false;
    zoomScale = 1;
    onClose?.();
  }

  function zoomIn() {
    zoomScale = Math.min(maxZoom, zoomScale + zoomStep);
  }

  function zoomOut() {
    zoomScale = Math.max(minZoom, zoomScale - zoomStep);
  }

  function resetZoom() {
    zoomScale = 1;
  }

  function navigate(dir: 1 | -1) {
    if (!totalSlides) return;
    if (galleryMode === 'review') {
      activeIndex = (activeIndex + dir + totalCount) % totalCount;
    } else {
      activeIndex = (activeIndex + dir + totalSlides) % totalSlides;
      selectedTile = has360ViewTile && activeIndex === 0 ? '360_VIEW' : 'PRODUCT_IMAGES';
    }
    zoomScale = 1;
  }

  function selectImageTile(index: number) {
    selectedTile = 'PRODUCT_IMAGES';
    activeIndex = has360ViewTile ? index + 1 : index;
    zoomScale = 1;
  }

  function selectTile(tileCode: string) {
    selectedTile = tileCode;
    activeIndex = tileCode === '360_VIEW' && has360ViewTile ? 0 : activeIndex;
    zoomScale = 1;
  }
</script>

<Dialog
  class="pdp-gallery-only-dialog"
  bind:open
  fullscreen
  aria-labelledby="gallery-title"
  on:SMUIDialog:closed={closeModal}
>
  <Header>
    <div class="gallery-header">
      <div id="gallery-title" class="gallery-count">{imageLabel}</div>
      <div class="gallery-actions">
        <button type="button" class="gallery-zoom-btn reset-btn" on:click={resetZoom} title="Reset zoom">
          <span class="icon-text"> <Icon type="font" glyph="reload" desktopSize={28} /> </span>
        </button>
        <button type="button" class="gallery-zoom-btn" on:click={zoomOut} disabled={is360 || zoomScale <= minZoom}>
          <span class="icon-text"> <Icon type="primary" glyph="icon-search-minus" desktopSize={28} /> </span>
        </button>
        <button type="button" class="gallery-zoom-btn" on:click={zoomIn} disabled={is360 || zoomScale >= maxZoom}>
          <span class="icon-text"> <Icon type="primary" glyph="icon-search-plus" desktopSize={28} /> </span>
        </button>
      </div>
      <div class="gallery-close">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <span class="close-icon" on:click={closeModal}>
          <Icon type="font" glyph="close" mobileSize={20} desktopSize={30} />
        </span>
      </div>
    </div>
  </Header>

  <div class="gallery-modal-content" class:only-360={is360}>
    {#if !is360}
      <button type="button" class="side-arrow left-arrow" on:click={() => navigate(-1)} aria-label="Previous image">
        <Icon
          type="font"
          glyph={lang === 'en' ? 'chevron_left' : 'chevron_right'}
          mobileSize={40}
          desktopSize={50}
        /></button
      >
    {/if}
    <div class="stage-wrap" class:stage-wrap-360={is360}>
      <div class="stage" class:stage-360={is360}>
        {#if is360}
          <PdpProductGallery360View {code} />
        {:else if currentItem?.type === 'video'}
          {@const source = [
            currentItem.src + '/webm_480p',
            currentItem.src + '/mp4_480p',
            currentItem.src + '/webm_1080p',
            currentItem.src + '/mp4_1080p',
          ]}
          <VideoPlayer poster={currentItem.src} {source} iconColor="#fff" barsBgColor="#73859f" color="#2b333fb3" />
        {:else if currentItem?.src}
          <!-- svelte-ignore a11y-img-redundant-alt -->
          <img
            class="stage-image"
            style:transform={`scale(${zoomScale})`}
            src={currentItem.src + '?fmt=auto&w=1400&sm=c'}
            alt={galleryImageAlt}
          />
        {/if}
      </div>
      <div class="thumb-strip">
        <div class="thumb-strip-inner">
          {#if has360ViewTile}
            <button
              type="button"
              class="thumb-item thumb-item-360 {is360 ? 'active' : ''}"
              on:mouseenter={() => selectTile('360_VIEW')}
              on:focus={() => selectTile('360_VIEW')}
              on:click={() => selectTile('360_VIEW')}
              aria-label="Open 360 view"
            >
              {#if data360View?.imageUrl}
                <img src={data360View.imageUrl + '?fmt=auto&sm=c'} alt={data360View.label || '360 view'} />
              {/if}
              <span class="thumb-360-label">{data360View?.label || '360 View'}</span>
            </button>
          {/if}
          {#each filteredGalleryImages as image, index}
            <button
              type="button"
              class="thumb-item {!is360 && activeIndex === index + (has360ViewTile ? 1 : 0) ? 'active' : ''}"
              on:mouseenter={() => selectImageTile(index)}
              on:focus={() => selectImageTile(index)}
              on:click={() => selectImageTile(index)}
              aria-label={`Open image ${index + 1}`}
            >
              <img src={image.src + '?fmt=auto&w=120&sm=c'} alt="Thumb" />
            </button>
          {/each}
        </div>
      </div>
    </div>
    {#if !is360}
      <button type="button" class="side-arrow right-arrow" on:click={() => navigate(1)} aria-label="Next image">
        <Icon type="font" glyph={lang === 'en' ? 'chevron_right' : 'chevron_left'} mobileSize={40} desktopSize={50} />
      </button>
    {/if}
  </div>
</Dialog>

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  :global(.pdp-gallery-only-dialog.mdc-dialog.mdc-dialog--fullscreen .mdc-dialog__surface) {
    width: 100vw;
    min-height: 100%;
    max-height: 100%;
    background-color: transparent;
    box-shadow: none;
  }

  :global(.pdp-gallery-only-dialog.mdc-dialog.mdc-dialog--fullscreen .mdc-dialog__container) {
    background: rgba(11, 11, 11, 0.4);
  }

  :global(.pdp-gallery-only-dialog.mdc-dialog.mdc-dialog--fullscreen .mdc-dialog__scrim) {
    background: rgba(15, 23, 42, 0.45);
  }

  :global(.pdp-gallery-only-dialog.mdc-dialog.mdc-dialog--fullscreen .mdc-dialog__header) {
    padding: 12px 16px;
    background: transparent;
    @include desktop-min-width {
      padding: 16px 32px;
    }
  }

  :global(.pdp-gallery-only-dialog.mdc-dialog.mdc-dialog--fullscreen .mdc-dialog__content) {
    padding: 0;
    overflow: hidden;
  }

  .gallery-header {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
  }

  .gallery-count {
    justify-self: start;
    color: $primary-white;
    font-size: 16px;
    line-height: 18px;
    font-weight: 700;
  }

  .gallery-actions {
    justify-self: center;
    display: flex;
    align-items: center;
    gap: 8px;
    :global(.c_icon_new) {
      color: $primary-white;
    }
    :global(.primary-icon),
    :global(.primary-icon use) {
      color: $primary-white;
      fill: $primary-white;
    }
  }

  .gallery-close {
    justify-self: end;
  }

  .gallery-zoom-btn {
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 0;
    background: transparent;
    color: $primary-white;
    font-size: 30px;
    line-height: 30px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  .icon-btn,
  .reset-btn {
    font-size: 60px;
  }

  .icon-text {
    position: relative;
    top: -1px;
  }

  .close-icon {
    cursor: pointer;
    color: $primary-white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 0;
    background: transparent;
  }

  .gallery-modal-content {
    position: relative;
    height: calc(100vh - 68px);
    display: grid;
    grid-template-columns: none;
    align-items: center;
    gap: 12px;
    padding: 0 12px;
    overflow: hidden;
    @include desktop-min-width {
      height: calc(100vh - 84px);
      grid-template-columns: minmax(72px, 1fr) minmax(0, 8fr) minmax(72px, 1fr);
      gap: 24px;
      padding: 0 24px;
    }
  }
  .gallery-modal-content.only-360 {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
    @include desktop-min-width {
      grid-template-columns: minmax(0, 1fr);
      padding: 0 24px 16px;
    }
  }

  .stage-wrap {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: $primary-white;
    border-radius: 8px;
  }
  .stage-wrap.stage-wrap-360 {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    @include desktop-min-width {
      width: 75%;
      max-width: 75%;
    }
  }
  .stage {
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    border-radius: 12px;
    background: $primary-white;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    @include desktop-min-width {
      border-radius: 18px;
    }
  }
  .stage.stage-360 {
    width: 100%;
    min-height: 62vh;
    display: block;
    padding: 0;
    margin: 0;
  }

  :global(.pdp-gallery-only-dialog.mdc-dialog.mdc-dialog--fullscreen .product-360-view-container) {
    width: 100%;
    min-height: 62vh;
    display: block;
    background: $primary-white;
    padding-bottom: 0;
    margin: 0;
    border-radius: 12px;
    overflow: hidden;
  }

  :global(.pdp-gallery-only-dialog.mdc-dialog.mdc-dialog--fullscreen .product-image-360) {
    display: block;
    width: 100%;
    min-width: 0;
    min-height: 62vh;
    height: 62vh;
    max-height: 72vh;
    margin: 0;
    border-radius: 0;
  }

  .stage-image {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    margin: auto;
    transform-origin: center center;
    transition: transform 180ms ease;
  }

  .side-arrow {
    width: 100%;
    max-width: 56px;
    height: 56px;
    border: none;
    border-radius: 0;
    background: transparent;
    color: $primary-white;
    font-size: 54px;
    line-height: 54px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    justify-self: center;
    text-shadow:
      0 1px 2px rgba(0, 0, 0, 0.65),
      0 0 8px rgba(56, 56, 56, 0.85);
    &.left-arrow {
      @include desktop-max-width {
        position: absolute;
        z-index: 99;
        left: 10px;
      }
    }
    &.right-arrow {
      @include desktop-max-width {
        position: absolute;
        z-index: 99;
        right: 10px;
      }
    }
  }

  .thumb-strip {
    overflow-x: auto;
    overflow-y: hidden;
    flex: 0 0 auto;
    padding: 20px;
    background: #f0f0f0;
    width: 100%;
    @include desktop-min-width {
      padding: 24px;
    }
  }

  .thumb-strip-inner {
    width: max-content;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 14px;
    @include desktop-min-width {
      gap: 20px;
    }
  }

  .thumb-item {
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.14);
    width: 56px;
    height: 56px;
    padding: 0;
    overflow: hidden;
    cursor: pointer;
    flex: 0 0 auto;
    @include desktop-min-width {
      width: 70px;
      height: 70px;
    }

    &.active {
      border: 1px solid $icon-only-icon-blue;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: $primary-white;
    }
  }

  .thumb-item-360 {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: $primary-white;
    border-color: #d7e4ef;

    img {
      width: 28px;
      height: 28px;
      object-fit: contain;
      background: transparent;
    }
  }

  .thumb-360-label {
    color: $neutral-dark-02;
    font-size: 9px;
    line-height: 10px;
    font-weight: 500;
    text-align: center;
  }
</style>
